// Cave84 -- Price Review Page (Phase 4 Week 13)
// Spec S4.7 F7.7. Owner resolves needs_owner_review queue entries:
//   Keep current price / Use suggested price / Enter manually.

import { useState } from 'react';
import type { GenerationQueueEntry } from '@cave84/shared-types';
import { usePriceReviewQueue, useResolveReviewEntry, useLatestPrice } from '@/hooks/useMarketPrices';
import { useVintage } from '@/hooks/useVintages';
import { useWine } from '@/hooks/useWines';
import { useProducer } from '@/hooks/useProducers';
import { Money } from '@/components/Money';

// --- Review card ------------------------------------------------------------------

function ReviewCard({ entry }: { entry: GenerationQueueEntry }) {
  const vintageId = entry.entity_id;
  const { data: vintage } = useVintage(vintageId);
  const { data: wine } = useWine(vintage?.wine_id);
  const { data: producer } = useProducer(wine?.producer_id);
  const { data: currentPrice } = useLatestPrice(vintageId);
  const resolveMutation = useResolveReviewEntry();

  const [mode, setMode] = useState<'idle' | 'manual'>('idle');
  const [manualPrice, setManualPrice] = useState<string>('');
  const [done, setDone] = useState(false);

  const resultData = entry.result_data as {
    found_quotes?: { price: number; currency: string }[];
    suggestion?: string;
  } | undefined;
  const suggestedQuote = resultData?.found_quotes?.[0];

  const resolve = (action: 'keep_current' | 'use_suggested' | 'manual_entry') => {
    let decision;
    if (action === 'keep_current') {
      decision = { action: 'keep_current' as const };
    } else if (action === 'use_suggested' && suggestedQuote) {
      decision = { action: 'use_suggested' as const, price: suggestedQuote.price, currency: 'EUR' as const };
    } else {
      const p = parseFloat(manualPrice);
      if (isNaN(p) || p <= 0) return;
      decision = { action: 'manual_entry' as const, price: p, currency: 'EUR' as const };
    }
    resolveMutation.mutate(
      { queueEntryId: entry.id, vintageId, decision },
      { onSuccess: () => setDone(true) }
    );
  };

  if (done) {
    return (
      <div className="bg-surface-elevated rounded-xl border border-emerald-800/50 px-5 py-4 flex items-center gap-3">
        <svg className="w-5 h-5 text-emerald-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <p className="text-sm text-text-secondary">
          {producer && <span className="text-text-secondary">{producer.name} · </span>}
          <span className="text-text-primary font-medium">{wine?.cuvee_name ?? '--'}</span>
          {vintage ? <span className="text-text-secondary ml-2" dir="ltr">{vintage.year}</span> : null}
          {' '} resolved.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-surface-elevated rounded-xl border border-border-default p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          {producer ? (
            <p className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">{producer.name}</p>
          ) : wine ? (
            <p className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">Loading…</p>
          ) : vintage ? (
            <p className="text-xs text-text-secondary uppercase tracking-wide mb-0.5">Wine not found</p>
          ) : null}
          <h3 className="font-semibold text-text-primary">
            {wine?.cuvee_name ?? (vintage ? '(wine missing)' : '(vintage not found)')}
          </h3>
          {vintage && (
            <p className="text-sm text-text-secondary mt-0.5" dir="ltr">{vintage.year}</p>
          )}
          {!vintage && (
            <p className="text-xs text-text-tertiary mt-0.5 font-mono">{vintageId}</p>
          )}
        </div>
        <span className="text-xs px-2 py-0.5 rounded-full bg-orange-900/40 text-orange-400 border border-orange-800 flex-shrink-0">
          Needs review
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-surface-default rounded-lg p-3 border border-border-default">
          <p className="text-xs text-text-secondary uppercase tracking-widest mb-1">Current</p>
          {currentPrice ? (
            <p className="text-lg font-semibold text-text-primary" dir="ltr">
              <Money value={currentPrice.price} currency="EUR" minWidthCh={6} />
            </p>
          ) : (
            <p className="text-sm text-text-secondary italic">No price yet</p>
          )}
        </div>
        <div className="bg-surface-default rounded-lg p-3 border border-border-default">
          <p className="text-xs text-text-secondary uppercase tracking-widest mb-1">Suggested</p>
          {suggestedQuote ? (
            <p className="text-lg font-semibold text-amber-300" dir="ltr">
              <Money value={suggestedQuote.price} currency="EUR" minWidthCh={6} />
            </p>
          ) : (
            <p className="text-sm text-text-secondary italic">None found</p>
          )}
        </div>
      </div>

      {resultData?.suggestion && (
        <p className="text-xs text-text-secondary bg-surface-default rounded-lg px-3 py-2 border border-border-default">
          {resultData.suggestion}
        </p>
      )}

      {mode === 'idle' ? (
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => resolve('keep_current')}
            disabled={resolveMutation.isPending}
            className="flex-1 px-3 py-2 rounded-lg border border-border-default text-sm text-text-secondary hover:text-text-primary hover:border-text-secondary transition-colors disabled:opacity-40"
          >
            Keep current
          </button>
          {suggestedQuote && (
            <button
              onClick={() => resolve('use_suggested')}
              disabled={resolveMutation.isPending}
              className="flex-1 px-3 py-2 rounded-lg border border-amber-800 text-sm text-amber-300 hover:bg-amber-900/20 transition-colors disabled:opacity-40"
            >
              Use <Money value={suggestedQuote.price} currency="EUR" minWidthCh={6} />
            </button>
          )}
          <button
            onClick={() => setMode('manual')}
            className="flex-1 px-3 py-2 rounded-lg border border-border-default text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            Manual entry
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-text-secondary">E</span>
            <input
              type="number"
              placeholder="0.00"
              value={manualPrice}
              onChange={(e) => setManualPrice(e.target.value)}
              className="w-full bg-surface-default border border-border-default rounded-lg pl-7 pr-3 py-2 text-sm text-text-primary focus:outline-none focus:border-accent-burgundy"
              ref={(el) => el?.focus()}
            />
          </div>
          <button
            onClick={() => resolve('manual_entry')}
            disabled={!manualPrice || isNaN(parseFloat(manualPrice)) || resolveMutation.isPending}
            className="px-4 py-2 rounded-lg bg-accent-burgundy text-white text-sm font-medium disabled:opacity-40"
          >
            Save
          </button>
          <button onClick={() => setMode('idle')} className="px-3 py-2 text-sm text-text-secondary hover:text-text-primary">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// --- Main page -------------------------------------------------------------------

export default function PriceReviewPage() {
  const { data: entries = [], isLoading, isError, error } = usePriceReviewQueue();

  return (
    <div className="min-h-screen bg-surface-default text-text-primary">
      <div className="border-b border-border-default bg-surface-elevated">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-5">
          <p className="text-xs text-text-secondary uppercase tracking-widest mb-1">Market Prices</p>
          <h1 className="text-2xl font-bold text-text-primary">Price Review</h1>
          <p className="text-sm text-text-secondary mt-1">
            Vintages where the batch could not find a confident price. Resolve each before the next refresh.
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">

        {isLoading && (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent-burgundy border-t-transparent" />
          </div>
        )}

        {isError && (
          <div className="rounded-xl border border-red-800/60 bg-red-900/20 px-5 py-4 space-y-2">
            <p className="text-sm font-semibold text-red-400">Query error -- check browser console</p>
            <p className="text-xs text-red-300 font-mono break-all">
              {(error as Error)?.message ?? String(error)}
            </p>
          </div>
        )}

        {!isLoading && !isError && entries.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64 gap-4 text-center">
            <div className="text-4xl">checkmark</div>
            <h3 className="text-lg font-semibold text-text-primary">No items to review</h3>
            <p className="text-sm text-text-secondary max-w-sm">
              All price refresh results have been resolved. Run a new refresh batch to generate more.
            </p>
          </div>
        )}

        {!isLoading && !isError && entries.length > 0 && (
          <div className="space-y-4">
            <p className="text-sm text-text-secondary mb-2">
              <span className="font-semibold text-text-primary">{entries.length}</span>
              {' '}item{entries.length !== 1 ? 's' : ''} awaiting your decision.
            </p>
            {entries.map((entry) => (
              <ReviewCard key={entry.id} entry={entry} />
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
